import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toPng } from "html-to-image";
import { Radio, Mic2, Coins, Globe2, Download, Share2, Sparkles, Smartphone, Bell, Gift, ArrowRight } from "lucide-react";

/** Facebook OG / Link Preview size — 1200×630. Designed to render as a single
 *  shareable image (no external fonts/assets so it captures cleanly client-side). */
export default function PromoCard() {
  const cardRef = useRef(null);
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!wrapRef.current) return;
    const fit = () => {
      const w = wrapRef.current?.clientWidth || 1200;
      setScale(Math.min(1, w / 1200));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const download = async (filename) => {
    if (!cardRef.current) return;
    setBusy(true);
    const prev = cardRef.current.style.transform;
    cardRef.current.style.transform = "scale(1)";
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#050505",
        width: 1200,
        height: 630,
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = filename;
      a.click();
    } catch (e) {
      console.error(e);
      alert("Couldn't generate the image. Try a different browser.");
    }
    cardRef.current.style.transform = prev;
    setBusy(false);
  };

  const shareText = "Launch your own internet radio station on Fliboe — AI DJ between every song, tip jar, worldwide sync, talk shows. No transmitter, no out-of-pocket cost. Tune in or go on-air at https://fliboe.com";

  const openFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("https://fliboe.com")}&quote=${encodeURIComponent(shareText)}`, "_blank");
  };

  return (
    <main className="max-w-6xl mx-auto px-5 py-10 pb-24" data-testid="promo-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-emerald-500">// SHARE KIT</div>
          <h1 className="fl-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1">Facebook promo card</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Download the 1200×630 image below and post it to your Facebook page. The card is
            sized to fit the link-preview area Facebook auto-renders when you paste your station URL.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => download("fliboe-promo-facebook.png")} disabled={busy} className="fl-btn !py-2.5 !px-4 text-sm" data-testid="promo-download">
            <Download className="w-4 h-4" /> {busy ? "Rendering…" : "Download PNG"}
          </button>
          <button onClick={openFacebook} className="fl-btn ghost !py-2.5 !px-4 text-sm" data-testid="promo-share-fb">
            <Share2 className="w-4 h-4" /> Open Facebook share
          </button>
          <Link to="/" className="fl-btn ghost !py-2.5 !px-4 text-sm">
            <ArrowRight className="w-4 h-4 rotate-180" /> Back
          </Link>
        </div>
      </div>

      {/* The downloadable artwork. Fixed at 1200x630 internally; auto-scaled to container width for preview. */}
      <div ref={wrapRef} className="rounded-xl overflow-hidden border border-emerald-500/20 bg-black shadow-2xl shadow-emerald-500/10" style={{ height: `${Math.round(630 * scale)}px` }}>
        <div
          ref={cardRef}
          style={{
            width: "1200px",
            height: "630px",
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            fontFamily: "'Work Sans', system-ui, sans-serif",
          }}
        >
          <PromoArtwork />
        </div>
      </div>

      {/* Caption + tips */}
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="fl-card p-5">
          <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-emerald-500 mb-2">// Suggested caption</div>
          <p className="text-sm text-slate-300 leading-relaxed">
            I just launched my own internet radio station on <strong>Fliboe</strong>. AI host between every song, a tip jar, and listeners worldwide in perfect sync. Free to launch — no transmitter, no fees beyond the plan.<br />
            Tune in: https://fliboe.com
          </p>
          <button
            onClick={() => { navigator.clipboard.writeText("I just launched my own internet radio station on Fliboe. AI host between every song, a tip jar, and listeners worldwide in perfect sync. Free to launch — no transmitter, no fees beyond the plan. Tune in: https://fliboe.com"); }}
            className="fl-btn ghost !py-2 !px-3 text-xs mt-3"
            data-testid="promo-copy-caption"
          >
            <Sparkles className="w-3.5 h-3.5" /> Copy caption
          </button>
        </div>
        <div className="fl-card p-5">
          <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-emerald-500 mb-2">// Best practices</div>
          <ul className="space-y-2 text-sm text-slate-300">
            <li>· Post the image <strong>and</strong> a link to your station — Facebook shows the image, the link drives clicks.</li>
            <li>· Pin it to the top of your page for the first 48 hours.</li>
            <li>· Reshare to community groups in your genre (lo-fi, country, talk, etc.).</li>
            <li>· Don't forget your referral code (Dashboard → Spread the signal) — every 3 friends who join gives you a free month of Premium.</li>
          </ul>
        </div>
      </div>

      {/* Mobile scale handler */}
      <style>{`/* legacy fallback */`}</style>
    </main>
  );
}

function PromoArtwork() {
  return (
    <div
      style={{
        width: "1200px",
        height: "630px",
        position: "relative",
        background:
          "radial-gradient(80% 60% at 100% 0%, rgba(245,158,11,0.18) 0%, transparent 60%), radial-gradient(60% 50% at 0% 100%, rgba(245,158,11,0.10) 0%, transparent 60%), linear-gradient(180deg, #0A0A0A 0%, #050505 100%)",
        color: "white",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* Faint scanlines */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: "repeating-linear-gradient(0deg, rgba(255,255,255,0.025) 0px, rgba(255,255,255,0.025) 1px, transparent 1px, transparent 4px)",
        pointerEvents: "none",
      }} />

      {/* Brand row */}
      <div style={{ position: "absolute", top: 48, left: 64, display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ position: "relative", width: 56, height: 56, borderRadius: 14, background: "rgba(245,158,11,0.15)", border: "2px solid rgba(245,158,11,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Radio style={{ width: 30, height: 30, color: "#F59E0B" }} strokeWidth={2.2} />
          <span style={{ position: "absolute", top: 6, right: 6, width: 10, height: 10, borderRadius: "50%", background: "#EF4444", boxShadow: "0 0 0 4px rgba(239,68,68,0.25)" }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontSize: 44, fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 1, fontFamily: "'Inter', system-ui, sans-serif" }}>FLIBOE</span>
          <span style={{ fontSize: 13, letterSpacing: "0.3em", color: "#94A3B8", fontFamily: "'JetBrains Mono', monospace", marginTop: 4 }}>/ ON AIR — WORLDWIDE</span>
        </div>
      </div>

      {/* Tagline pill (top-right) */}
      <div style={{
        position: "absolute", top: 60, right: 64,
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: "10px 18px", borderRadius: 999,
        background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.45)",
        color: "#F59E0B", fontSize: 14, fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase",
      }}>
        <Sparkles style={{ width: 16, height: 16 }} /> 100% free to launch
      </div>

      {/* Main headline */}
      <div style={{ position: "absolute", left: 64, top: 168, width: 660 }}>
        <div style={{ fontSize: 88, fontWeight: 900, lineHeight: 0.94, letterSpacing: "-0.045em", fontFamily: "'Inter', system-ui, sans-serif" }}>
          Your own<br />
          <span style={{ color: "#F59E0B" }}>radio station.</span><br />
          Worldwide.
        </div>
        <div style={{ marginTop: 22, fontSize: 22, color: "#CBD5E1", lineHeight: 1.4, maxWidth: 580 }}>
          AI host on the mic between every song. Tip jar that pays 100% to you.
          Sync-locked listeners on every continent.
        </div>
      </div>

      {/* Feature grid (right column) */}
      <div style={{
        position: "absolute", right: 64, top: 178, width: 420,
        display: "grid", gridTemplateColumns: "1fr", gap: 14,
      }}>
        <FeatureBadge icon={<Mic2 style={{ width: 22, height: 22, color: "#F59E0B" }} />} title="AI DJ between every song" sub="Voices switch with time of day" />
        <FeatureBadge icon={<Coins style={{ width: 22, height: 22, color: "#F59E0B" }} />} title="Tip jar — 100% to you" sub="PayPal · Cash App · Venmo" />
        <FeatureBadge icon={<Globe2 style={{ width: 22, height: 22, color: "#F59E0B" }} />} title="Worldwide sync engine" sub="Everyone hears the same song, in unison" />
        <FeatureBadge icon={<Smartphone style={{ width: 22, height: 22, color: "#F59E0B" }} />} title="Installable on iOS & Android" sub="PWA — no app store needed" />
        <FeatureBadge icon={<Bell style={{ width: 22, height: 22, color: "#F59E0B" }} />} title="Push alerts for new shows" sub="Your listeners come back" />
        <FeatureBadge icon={<Gift style={{ width: 22, height: 22, color: "#F59E0B" }} />} title="3 referrals = 1 free month" sub="Premium on the house" />
      </div>

      {/* CTA bar */}
      <div style={{
        position: "absolute", left: 64, bottom: 56,
        display: "flex", alignItems: "center", gap: 16,
      }}>
        <div style={{
          padding: "18px 30px", borderRadius: 999, background: "#F59E0B", color: "#0A0A0A",
          fontWeight: 800, fontSize: 22, letterSpacing: "-0.01em",
          display: "inline-flex", alignItems: "center", gap: 10,
          fontFamily: "'Inter', system-ui, sans-serif",
        }}>
          <Radio style={{ width: 22, height: 22 }} /> Launch yours at fliboe.com
        </div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "#94A3B8", letterSpacing: "0.25em", textTransform: "uppercase" }}>
          / sign up · go on-air · in under 5 minutes
        </div>
      </div>

      {/* Equalizer flourish (bottom-right) */}
      <div style={{ position: "absolute", right: 64, bottom: 56, display: "flex", alignItems: "flex-end", gap: 6, height: 70 }}>
        {[42, 64, 30, 54, 70, 38, 58, 28, 48, 62, 34, 52].map((h, i) => (
          <span key={i} style={{ width: 6, borderRadius: 3, background: i % 2 === 0 ? "#F59E0B" : "#facc15", height: h, opacity: 0.85 }} />
        ))}
      </div>
    </div>
  );
}

function FeatureBadge({ icon, title, sub }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 14,
      padding: "14px 16px", borderRadius: 14,
      background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
      backdropFilter: "blur(8px)",
    }}>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: "white", lineHeight: 1.15 }}>{title}</div>
        <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 2 }}>{sub}</div>
      </div>
    </div>
  );
}
